/**
 * PatrolModal Component
 *
 * Modal for creating and editing patrol assignments.
 * Features:
 * - Guard, company, location selection with cascading dropdowns
 * - Checklist assignment (global and location-specific)
 * - Date, start time, and duration inputs
 * - Conflict detection and warnings
 * - Split shift and long shift warnings
 */

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Tooltip,
  Divider,
  IconButton,
  Autocomplete,
  Chip,
  ListSubheader,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ChecklistIcon from '@mui/icons-material/Checklist';
import moment from 'moment';
import { MobileTimePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { fetchCompanies } from 'services/api/companyService';
import { fetchLocationsByCompany } from 'services/api/locationService';
import { fetchGuards } from 'services/api/guardService';
import {
  fetchGlobalChecklists,
  fetchLocationChecklists,
  transformChecklistsForApi,
  transformChecklistsFromApi,
} from 'services/api/checklistService';
import { detectConflicts, formatConflictSummary } from 'utils/conflictDetection';
import { classifyConflicts, rescheduleSubsequentPatrols } from 'utils/shiftRescheduler';
import {
  SPLIT_SHIFT_MIN_GAP_HOURS,
  SPLIT_SHIFT_MAX_GAP_HOURS,
  LONG_SHIFT_THRESHOLD_HOURS,
  SHIFT_GAP_THRESHOLD_MINUTES,
  MIN_PATROL_DURATION_HOURS,
  PATROL_DURATION_STEP_HOURS,
} from '../constants';
import {
  UnsavedChangesDialog,
  SplitShiftWarningDialog,
  LongShiftWarningDialog,
  ConflictWarningDialog,
  DeletePatrolDialog,
  ValidationErrorDialog,
  ShiftOverlapDialog,
} from './dialogs';

const PatrolModal = ({
  open,
  onClose,
  patrol,
  onSave,
  onDelete,
  autofillCustomerName,
  autofillAddressName,
  autofillContactId,
  autofillContactName,
  autofillDate,
  autofillStartTime,
  autofillDuration,
  currentShift,
  existingPatrols = [],
}) => {
  const isEditMode = !!patrol;
  const { notifyError } = useNotificationProvider();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form state — IDs are the source of truth; names are for display
  const [formData, setFormData] = useState({
    ContactId: null,
    ContactName: '',
    CustomerId: null,
    CustomerName: '',
    AddressId: null,
    AddressName: '',
    date: null,
    startTime: '',
    duration: '',
    checklists: [],
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const [showSplitShiftWarning, setShowSplitShiftWarning] = useState(false);
  const [showLongShiftWarning, setShowLongShiftWarning] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictSummary, setConflictSummary] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [pendingPatrolData, setPendingPatrolData] = useState(null);
  const [splitShiftGapHours, setSplitShiftGapHours] = useState(0);
  const [showOverlapDialog, setShowOverlapDialog] = useState(false);
  const [overlapInfo, setOverlapInfo] = useState(null);
  const [pendingRescheduledPatrols, setPendingRescheduledPatrols] = useState([]);

  // Data from API
  const [guards, setGuards] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [globalChecklists, setGlobalChecklists] = useState([]);
  const [locationChecklists, setLocationChecklists] = useState([]);
  const [checklistsLoading, setChecklistsLoading] = useState(false);

  // Fetch companies, guards, and global checklists when modal opens
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const loadInitialData = async () => {
      try {
        const [companiesData, guardsData, globalChecklistsData] = await Promise.all([
          fetchCompanies(),
          fetchGuards(),
          fetchGlobalChecklists(),
        ]);
        if (isMounted) {
          setCompanies(companiesData);
          setGuards(guardsData);
          setGlobalChecklists(globalChecklistsData);
        }
      } catch (error) {
        console.error('[PatrolModal] Failed to load initial data:', error);
        if (isMounted) {
          notifyError('Failed to load form data. Please try again.');
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [open, notifyError]);

  // Fetch locations when company changes
  useEffect(() => {
    if (!formData.CustomerId) {
      setAvailableLocations([]);
      return;
    }

    let isMounted = true;
    setLoadingLocations(true);

    const loadLocations = async () => {
      try {
        const locationsData = await fetchLocationsByCompany(formData.CustomerId);
        if (isMounted) {
          setAvailableLocations(locationsData);
        }
      } catch (error) {
        console.error('[PatrolModal] Failed to load locations:', { companyId: formData.CustomerId, error });
        if (isMounted) {
          setAvailableLocations([]);
          notifyError('Failed to load locations. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoadingLocations(false);
        }
      }
    };

    loadLocations();

    return () => {
      isMounted = false;
    };
  }, [formData.CustomerId, notifyError]);

  // Fetch location-specific checklists when location changes
  useEffect(() => {
    if (!formData.CustomerId || !formData.AddressId) {
      setLocationChecklists([]);
      return;
    }

    let isMounted = true;

    const loadLocationChecklists = async () => {
      setChecklistsLoading(true);
      try {
        const checklistsData = await fetchLocationChecklists(formData.CustomerId, formData.AddressId);
        if (isMounted) {
          setLocationChecklists(checklistsData);
        }
      } catch (error) {
        console.error('[PatrolModal] Failed to load location checklists:', error);
        if (isMounted) {
          setLocationChecklists([]);
          notifyError('Failed to load checklists. Please try again.');
        }
      } finally {
        if (isMounted) {
          setChecklistsLoading(false);
        }
      }
    };

    loadLocationChecklists();

    return () => {
      isMounted = false;
    };
  }, [formData.CustomerId, formData.AddressId]);

  // Helper to get next nearest hour
  const getNextNearestHour = () => {
    return moment().add(1, 'hour').startOf('hour').toDate();
  };

  const calculateDuration = (start, end) => {
    const duration = moment(end).diff(moment(start), 'hours', true);
    return duration.toString();
  };

  // Initialize form data when patrol changes
  useEffect(() => {
    if (patrol) {
      const existingChecklists = transformChecklistsFromApi(patrol.Checklists);
      const data = {
        ContactId: patrol.ContactId || null,
        ContactName: patrol.ContactName || '',
        CustomerId: patrol.CustomerId || null,
        CustomerName: patrol.CustomerName || '',
        AddressId: patrol.AddressId || null,
        AddressName: patrol.AddressName || '',
        date: moment(patrol.StartTime).startOf('day').toDate(),
        startTime: moment(patrol.StartTime).toDate(),
        duration: calculateDuration(patrol.StartTime, patrol.EndTime),
        checklists: existingChecklists,
      };
      setFormData(data);
      setInitialFormData(data);
    } else {
      const emptyData = {
        ContactId: autofillContactId || null,
        ContactName: autofillContactName || '',
        CustomerId: null,
        CustomerName: autofillCustomerName || '',
        AddressId: null,
        AddressName: autofillAddressName || '',
        date: autofillDate
          ? moment(autofillDate, 'YYYY-MM-DD').startOf('day').toDate()
          : moment().startOf('day').toDate(),
        startTime: autofillStartTime
          ? moment(autofillStartTime, 'HH:mm').toDate()
          : getNextNearestHour(),
        duration: autofillDuration || '1',
        checklists: [],
      };
      setFormData(emptyData);
      setInitialFormData(emptyData);
    }
  }, [patrol, open, autofillCustomerName, autofillAddressName, autofillContactId, autofillContactName, autofillDate, autofillStartTime, autofillDuration]);

  const hasUnsavedChanges = useMemo(() => {
    if (!initialFormData) return false;

    const checklistsChanged = () => {
      const currentIds = (formData.checklists || []).map((c) => c.id).sort();
      const initialIds = (initialFormData.checklists || []).map((c) => c.id).sort();
      return JSON.stringify(currentIds) !== JSON.stringify(initialIds);
    };

    return (
      formData.ContactId !== initialFormData.ContactId ||
      formData.CustomerId !== initialFormData.CustomerId ||
      formData.AddressId !== initialFormData.AddressId ||
      (formData.date?.getTime?.() ?? null) !== (initialFormData.date?.getTime?.() ?? null) ||
      (formData.startTime?.getTime?.() ?? null) !== (initialFormData.startTime?.getTime?.() ?? null) ||
      formData.duration !== initialFormData.duration ||
      checklistsChanged()
    );
  }, [formData, initialFormData]);

  const isPastDate = useMemo(() => {
    if (!formData.date) return false;
    const selectedDate = moment(formData.date).startOf('day');
    const today = moment().startOf('day');
    return selectedDate.isBefore(today);
  }, [formData.date]);

  const handleFieldChange = useCallback((field, value, extraFields = {}) => {
    setFormData((prev) => {
      if (field === 'CustomerId' && prev.CustomerId !== value) {
        return {
          ...prev,
          CustomerId: value,
          CustomerName: extraFields.CustomerName ?? '',
          AddressId: null,
          AddressName: '',
          checklists: [],
        };
      }
      if (field === 'AddressId' && prev.AddressId !== value) {
        const globalOnly = (prev.checklists || []).filter((c) => c.group === 'Global');
        return {
          ...prev,
          AddressId: value,
          AddressName: extraFields.AddressName ?? '',
          checklists: globalOnly,
        };
      }
      if (field === 'ContactId') {
        return {
          ...prev,
          ContactId: value,
          ContactName: extraFields.ContactName ?? '',
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  }, []);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedPrompt(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowUnsavedPrompt(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowUnsavedPrompt(false);
  }, []);

  const handleReset = useCallback(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  // Helper to check for shift warnings when adding a patrol
  const checkShiftWarnings = useCallback((patrolData) => {
    if (isEditMode || !currentShift || !currentShift.patrols || currentShift.patrols.length === 0) {
      return { splitShift: false, longShift: false };
    }

    const newPatrolStart = moment(patrolData.StartTime);
    const newPatrolEnd = moment(patrolData.EndTime);
    const newPatrolDurationHours = newPatrolEnd.diff(newPatrolStart, 'hours', true);

    const sortedPatrols = [...currentShift.patrols].sort(
      (a, b) => moment(b.EndTime).diff(moment(a.EndTime))
    );
    const lastPatrol = sortedPatrols[0];
    const lastPatrolEnd = moment(lastPatrol.EndTime);

    let splitShiftWarning = false;
    let gapHours = 0;
    if (String(patrolData.ContactId) === String(currentShift.ContactId)) {
      gapHours = newPatrolStart.diff(lastPatrolEnd, 'hours', true);
      if (gapHours > SPLIT_SHIFT_MIN_GAP_HOURS && gapHours < SPLIT_SHIFT_MAX_GAP_HOURS) {
        splitShiftWarning = true;
      }
    }

    let longShiftWarning = false;
    let newTotalDuration = newPatrolDurationHours;
    if (String(patrolData.ContactId) === String(currentShift.ContactId)) {
      // Only warn if the new patrol is contiguous with the current shift
      // (i.e. the gap between the new patrol and the shift is within the shift gap threshold)
      const shiftStart = moment(currentShift.StartTime);
      const shiftEnd = moment(currentShift.EndTime);
      const gapAfterShift = newPatrolStart.diff(shiftEnd, 'minutes');
      const gapBeforeShift = shiftStart.diff(newPatrolEnd, 'minutes');
      const isContiguous = (gapAfterShift >= 0 && gapAfterShift < SHIFT_GAP_THRESHOLD_MINUTES)
        || (gapBeforeShift >= 0 && gapBeforeShift < SHIFT_GAP_THRESHOLD_MINUTES)
        || newPatrolStart.isBetween(shiftStart, shiftEnd, null, '[]');

      if (isContiguous) {
        const currentShiftDuration = currentShift.totalDuration || 0;
        newTotalDuration = currentShiftDuration + newPatrolDurationHours;
        if (newTotalDuration > LONG_SHIFT_THRESHOLD_HOURS) {
          longShiftWarning = true;
        }
      }
    }

    return {
      splitShift: splitShiftWarning,
      longShift: longShiftWarning,
      gapHours: gapHours,
      totalDuration: newTotalDuration
    };
  }, [isEditMode, currentShift]);

  // Actually save the patrol
  const performSave = useCallback((patrolData, rescheduledPatrols = []) => {
    onSave(patrolData, rescheduledPatrols);
    onClose();
    setShowSplitShiftWarning(false);
    setShowLongShiftWarning(false);
    setShowConflictWarning(false);
    setConflictSummary(null);
    setPendingPatrolData(null);
    setShowOverlapDialog(false);
    setOverlapInfo(null);
    setPendingRescheduledPatrols([]);
  }, [onSave, onClose]);

  const handleSave = useCallback(() => {
    if (
      !formData.ContactId ||
      !formData.CustomerId ||
      !formData.AddressId ||
      !formData.date ||
      !formData.startTime ||
      !formData.duration
    ) {
      setShowValidationError(true);
      return;
    }

    const startDateTime = moment(formData.date).set({
      hour: formData.startTime.getHours(),
      minute: formData.startTime.getMinutes(),
      second: 0,
      millisecond: 0,
    });
    const endDateTime = startDateTime.clone().add(parseFloat(formData.duration), 'hours');

    const patrolData = {
      ScheduleAssignmentId: patrol?.ScheduleAssignmentId || null,
      ContactId: formData.ContactId,
      ContactName: formData.ContactName,
      CustomerId: formData.CustomerId,
      CustomerName: formData.CustomerName,
      AddressId: formData.AddressId,
      AddressName: formData.AddressName,
      StartTime: startDateTime.toDate(),
      EndTime: endDateTime.toDate(),
      Checklists: transformChecklistsForApi(formData.checklists),
    };

    // Conflict detection — work with raw conflicts, format only at point of use
    const rawConflicts = detectConflicts(patrolData, existingPatrols);
    if (rawConflicts.length > 0) {
      setPendingPatrolData(patrolData);

      if (isEditMode) {
        // Classify into pushback-eligible vs generic
        const { pushbackConflicts, genericConflicts, overlapInfo: overlap } =
          classifyConflicts(patrolData, rawConflicts, existingPatrols);

        if (pushbackConflicts.length > 0) {
          // Show shift overlap dialog first; stash generic conflicts for chaining
          setOverlapInfo(overlap);
          setConflictSummary(
            genericConflicts.length > 0 ? formatConflictSummary(genericConflicts) : null
          );
          setShowOverlapDialog(true);
          return;
        }
      }

      // No pushback-eligible conflicts (or create mode) — show generic dialog
      setConflictSummary(formatConflictSummary(rawConflicts));
      setShowConflictWarning(true);
      return;
    }

    const warnings = checkShiftWarnings(patrolData);

    if (warnings.splitShift) {
      setPendingPatrolData(patrolData);
      setSplitShiftGapHours(warnings.gapHours);
      setShowSplitShiftWarning(true);
      return;
    }

    if (warnings.longShift) {
      setPendingPatrolData(patrolData);
      setShowLongShiftWarning(true);
      return;
    }

    performSave(patrolData);
  }, [formData, patrol, isEditMode, existingPatrols, checkShiftWarnings, performSave]);

  const handleConfirmSplitShift = useCallback(() => {
    setShowSplitShiftWarning(false);

    if (pendingPatrolData) {
      const warnings = checkShiftWarnings(pendingPatrolData);
      if (warnings.longShift) {
        setShowLongShiftWarning(true);
        return;
      }
      performSave(pendingPatrolData, pendingRescheduledPatrols);
    }
  }, [pendingPatrolData, pendingRescheduledPatrols, checkShiftWarnings, performSave]);

  const handleConfirmLongShift = useCallback(() => {
    setShowLongShiftWarning(false);
    if (pendingPatrolData) {
      performSave(pendingPatrolData, pendingRescheduledPatrols);
    }
  }, [pendingPatrolData, pendingRescheduledPatrols, performSave]);

  const handleConfirmConflict = useCallback(() => {
    setShowConflictWarning(false);
    setConflictSummary(null);

    if (pendingPatrolData) {
      const warnings = checkShiftWarnings(pendingPatrolData);

      if (warnings.splitShift) {
        setSplitShiftGapHours(warnings.gapHours);
        setShowSplitShiftWarning(true);
        return;
      }

      if (warnings.longShift) {
        setShowLongShiftWarning(true);
        return;
      }

      performSave(pendingPatrolData, pendingRescheduledPatrols);
    }
  }, [pendingPatrolData, pendingRescheduledPatrols, checkShiftWarnings, performSave]);

  // Handle overlap dialog: user clicks "Yes, Push Back Patrols"
  const handleOverlapConfirm = useCallback((preserveGaps) => {
    if (pendingPatrolData && overlapInfo) {
      const rescheduled = rescheduleSubsequentPatrols(
        overlapInfo.editedPatrol,
        overlapInfo.originalPatrol,
        overlapInfo.subsequentPatrols,
        preserveGaps
      );
      setShowOverlapDialog(false);
      setOverlapInfo(null);

      // If there are remaining generic conflicts, chain to conflict dialog
      if (conflictSummary && conflictSummary.totalCount > 0) {
        setPendingRescheduledPatrols(rescheduled);
        setShowConflictWarning(true);
        return;
      }

      performSave(pendingPatrolData, rescheduled);
    }
  }, [pendingPatrolData, overlapInfo, conflictSummary, performSave]);

  // Handle overlap dialog: user clicks "No, Keep Overlapping"
  const handleOverlapDecline = useCallback(() => {
    setShowOverlapDialog(false);
    setOverlapInfo(null);

    // If there are remaining generic conflicts, chain to conflict dialog
    if (conflictSummary && conflictSummary.totalCount > 0) {
      setShowConflictWarning(true);
      return;
    }

    if (pendingPatrolData) {
      performSave(pendingPatrolData);
    }
  }, [pendingPatrolData, conflictSummary, performSave]);

  const handleCancelWarning = useCallback(() => {
    setShowSplitShiftWarning(false);
    setShowLongShiftWarning(false);
    setShowConflictWarning(false);
    setConflictSummary(null);
    setPendingPatrolData(null);
    setShowOverlapDialog(false);
    setOverlapInfo(null);
    setPendingRescheduledPatrols([]);
  }, []);

  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    onDelete(patrol.ScheduleAssignmentId);
    onClose();
  }, [patrol?.ScheduleAssignmentId, onDelete, onClose]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            py: isMobile ? 1.5 : 1,
            mb: 1,
            // Safe area padding for mobile devices with notches
            ...(isMobile && {
              paddingTop: 'max(12px, env(safe-area-inset-top))',
            }),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center'}}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEditMode ? 'Edit Patrol' : 'New Patrol'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size={isMobile ? 'medium' : 'small'}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Assignment Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
            {/* Guard */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <PersonIcon sx={{ color: 'action.active', mt: 2 }} />
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Guard"
                  value={formData.ContactId || ''}
                  onChange={(e) => {
                    const guard = guards.find((g) => g.id === e.target.value);
                    handleFieldChange('ContactId', e.target.value, { ContactName: guard?.name || '' });
                  }}
                  fullWidth
                  required
                  size="medium"
                >
                  <MenuItem value="">
                    <em>Select Guard</em>
                  </MenuItem>
                  {guards.map((guard) => (
                    <MenuItem key={guard.id} value={guard.id}>
                      {guard.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {/* Company */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <BusinessIcon sx={{ color: 'action.active', mt: 2 }} />
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Company"
                  value={formData.CustomerId || ''}
                  onChange={(e) => {
                    const company = companies.find((c) => c.id === e.target.value);
                    handleFieldChange('CustomerId', e.target.value, { CustomerName: company?.name || '' });
                  }}
                  fullWidth
                  required
                  size="medium"
                >
                  <MenuItem value="">
                    <em>Select Company</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <LocationOnIcon sx={{ color: 'action.active', mt: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Tooltip
                  title={!formData.CustomerId ? 'Select a company first' : ''}
                  disableHoverListener={!!formData.CustomerId}
                  placement="top"
                >
                  <div>
                    <TextField
                      select
                      label="Location"
                      value={formData.AddressId || ''}
                      onChange={(e) => {
                        const loc = availableLocations.find((l) => l.id === e.target.value);
                        handleFieldChange('AddressId', e.target.value, { AddressName: loc?.name || '' });
                      }}
                      fullWidth
                      required
                      disabled={!formData.CustomerId || loadingLocations}
                      size="medium"
                      helperText={!formData.CustomerId ? 'Select a company first' : ''}
                      InputProps={{
                        endAdornment: loadingLocations ? (
                          <CircularProgress color="inherit" size={16} sx={{ mr: 2 }} />
                        ) : null,
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Location</em>
                      </MenuItem>
                      {availableLocations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </Tooltip>
              </Box>
            </Box>

            {/* Checklists */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <ChecklistIcon sx={{ color: 'action.active', mt: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Tooltip
                  title={!formData.AddressId ? 'Select a location first to see location-specific checklists' : ''}
                  disableHoverListener={!!formData.AddressId}
                  placement="top"
                >
                  <Autocomplete
                    multiple
                    options={[...locationChecklists, ...globalChecklists]}
                    value={formData.checklists}
                    onChange={(_, newValue) => handleFieldChange('checklists', newValue)}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    groupBy={(option) => option.group}
                    loading={checklistsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Checklists"
                        placeholder={formData.checklists.length === 0 ? 'Select checklists (optional)' : ''}
                        size="medium"
                        helperText={
                          !formData.AddressId
                            ? 'Select a location to see location-specific checklists'
                            : locationChecklists.length === 0 && globalChecklists.length === 0
                            ? 'No checklists available'
                            : ''
                        }
                      />
                    )}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <ListSubheader
                          component="div"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: 'background.paper',
                            color: params.group === 'Location' ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {params.group === 'Location' ? 'Location-Specific' : 'Global'}
                        </ListSubheader>
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name}
                          size="small"
                          color={option.group === 'Location' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      ))
                    }
                    noOptionsText={
                      checklistsLoading
                        ? 'Loading checklists...'
                        : !formData.AddressId
                        ? 'Select a location to see location-specific checklists'
                        : 'No checklists available'
                    }
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Schedule Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Date */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CalendarTodayIcon sx={{ color: 'action.active', mt: 2 }} />
              <Box sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <MobileDatePicker
                    label="Date"
                    value={formData.date}
                    onChange={(newDate) => handleFieldChange('date', newDate)}
                    minDate={new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        size="medium"
                        error={isPastDate}
                        helperText={isPastDate ? 'Cannot schedule patrols in the past' : ''}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            {/* Start Time and Duration in a row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                <AccessTimeIcon sx={{ color: 'action.active', mt: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileTimePicker
                      label="Start Time"
                      value={formData.startTime}
                      onChange={(newTime) => handleFieldChange('startTime', newTime)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          size="medium"
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                <TimerIcon sx={{ color: 'action.active', mt: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Duration (hours)"
                    type="number"
                    value={formData.duration}
                    onChange={(e) => handleFieldChange('duration', e.target.value)}
                    fullWidth
                    required
                    size="medium"
                    inputProps={{
                      min: MIN_PATROL_DURATION_HOURS,
                      step: PATROL_DURATION_STEP_HOURS,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1.5 : 0,
            // Safe area padding for mobile devices
            ...(isMobile && {
              paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
            }),
          }}
        >
          <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
            {isEditMode && (
              <Button
                onClick={handleDelete}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                sx={{ mr: isMobile ? 0 : 1 }}
                fullWidth={isMobile}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: isMobile ? '100%' : 'auto',
              flexDirection: isMobile ? 'column-reverse' : 'row',
            }}
          >
            <Button
              onClick={handleReset}
              variant="outlined"
              disabled={!hasUnsavedChanges}
              startIcon={<RestartAltIcon />}
              fullWidth={isMobile}
            >
              Reset
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              fullWidth={isMobile}
              disabled={isPastDate}
            >
              {isEditMode ? 'Save Changes' : 'Create Patrol'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Extracted Dialogs */}
      <UnsavedChangesDialog
        open={showUnsavedPrompt}
        onContinueEditing={handleCancelClose}
        onDiscard={handleConfirmClose}
      />

      <SplitShiftWarningDialog
        open={showSplitShiftWarning}
        gapHours={splitShiftGapHours}
        onCancel={handleCancelWarning}
        onConfirm={handleConfirmSplitShift}
      />

      <LongShiftWarningDialog
        open={showLongShiftWarning}
        thresholdHours={LONG_SHIFT_THRESHOLD_HOURS}
        onCancel={handleCancelWarning}
        onConfirm={handleConfirmLongShift}
      />

      <ConflictWarningDialog
        open={showConflictWarning}
        conflictSummary={conflictSummary}
        onCancel={handleCancelWarning}
        onConfirm={handleConfirmConflict}
      />

      <DeletePatrolDialog
        open={showDeleteConfirm}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <ValidationErrorDialog
        open={showValidationError}
        onClose={() => setShowValidationError(false)}
      />

      <ShiftOverlapDialog
        open={showOverlapDialog}
        editedPatrolLocation={overlapInfo?.editedPatrol?.AddressName}
        affectedPatrols={overlapInfo?.subsequentPatrols}
        overlapMinutes={overlapInfo?.overlapMinutes}
        onDecline={handleOverlapDecline}
        onConfirm={handleOverlapConfirm}
      />
    </>
  );
};

PatrolModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patrol: PropTypes.shape({
    ScheduleAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ContactName: PropTypes.string,
    ContactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    CustomerName: PropTypes.string,
    CustomerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    AddressName: PropTypes.string,
    AddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    Checklists: PropTypes.array,
  }),
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  autofillCustomerName: PropTypes.string,
  autofillAddressName: PropTypes.string,
  autofillContactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autofillContactName: PropTypes.string,
  autofillDate: PropTypes.string,
  autofillStartTime: PropTypes.string,
  autofillDuration: PropTypes.string,
  currentShift: PropTypes.shape({
    ContactName: PropTypes.string,
    patrols: PropTypes.array,
    totalDuration: PropTypes.number,
  }),
  existingPatrols: PropTypes.arrayOf(
    PropTypes.shape({
      ScheduleAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ContactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ContactName: PropTypes.string,
      AddressName: PropTypes.string,
      StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    })
  ),
};

export default memo(PatrolModal);
